import { useProvinceCity } from '../hooks/useProvinceCity';

/**
 * Reusable province -> city dropdown pair.
 *
 * Usage:
 *   <ProvinceCitySelect
 *     initialCityId={formData.location_id}
 *     onChange={(locationId) => setFormData(prev => ({ ...prev, location_id: locationId }))}
 *   />
 *
 * Pass `overrideCityId` when an external event (e.g. a map pin) needs to set
 * both selects programmatically; it re-triggers the resolve effect in the hook.
 */
export default function ProvinceCitySelect({
  initialCityId = null,
  overrideCityId = null,
  onChange,
  required = false,
  idPrefix = 'location',
}) {
  const {
    provinces,
    cities,
    provinceId,
    cityId,
    loadingProvinces,
    loadingCities,
    resolvingInitial,
    error,
    selectProvince,
    selectCity,
  } = useProvinceCity({ initialCityId, overrideCityId });

  const handleProvinceChange = (event) => {
    selectProvince(event.target.value);
    onChange?.(null);
  };

  const handleCityChange = (event) => {
    const id = event.target.value ? Number(event.target.value) : null;
    selectCity(id);
    onChange?.(id);
  };

  return (
    <div className="form-row">
      <div className="form-group">
        <label htmlFor={`${idPrefix}-province`}>استان{required && ' *'}</label>
        <select
          id={`${idPrefix}-province`}
          value={provinceId ?? ''}
          onChange={handleProvinceChange}
          disabled={loadingProvinces || resolvingInitial}
          required={required}
        >
          <option value="">
            {loadingProvinces || resolvingInitial ? 'در حال بارگذاری...' : 'انتخاب استان'}
          </option>
          {provinces.map((province) => (
            <option key={province.id} value={province.id}>
              {province.name_fa}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor={`${idPrefix}-city`}>شهر{required && ' *'}</label>
        <select
          id={`${idPrefix}-city`}
          value={cityId ?? ''}
          onChange={handleCityChange}
          disabled={!provinceId || loadingCities || resolvingInitial}
          required={required}
        >
          <option value="">
            {!provinceId
              ? 'ابتدا استان را انتخاب کنید'
              : loadingCities
                ? 'در حال بارگذاری...'
                : 'انتخاب شهر'}
          </option>
          {cities.map((city) => (
            <option key={city.id} value={city.id}>
              {city.name_fa}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}